import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AUTH_STORAGE_KEYS } from '../config/auth-storage.keys';
import type { LogoutResponse, PotatoLoginResponse } from '../models/login-response';
import {
  generateCodeChallenge,
  generateCodeVerifier,
  generateOAuthState,
} from '../utils/pkce.util';

@Injectable({
  providedIn: 'root',
})
export class AuthApi {
  private readonly http = inject(HttpClient);

  get redirectUri(): string {
    return `${window.location.origin}/login/callback`;
  }

  async startOAuthLogin(): Promise<void> {
    const { clientId, baseUrl, scopes } = environment.potato;

    if (!clientId) {
      throw new Error(
        'Potato clientId is not configured. Set environment.potato.clientId in environment.ts.'
      );
    }

    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateOAuthState();

    sessionStorage.setItem(AUTH_STORAGE_KEYS.codeVerifier, codeVerifier);
    sessionStorage.setItem(AUTH_STORAGE_KEYS.oauthState, state);

    const authUrl = new URL(`${baseUrl}/auth`);
    authUrl.searchParams.set('client_id', clientId);
    authUrl.searchParams.set('redirect_uri', this.redirectUri);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', scopes);
    authUrl.searchParams.set('code_challenge', codeChallenge);
    authUrl.searchParams.set('code_challenge_method', 'S256');
    authUrl.searchParams.set('state', state);

    window.location.href = authUrl.toString();
  }

  exchangeAuthorizationCode(code: string, codeVerifier: string): Promise<PotatoLoginResponse> {
    return firstValueFrom(
      this.http.post<PotatoLoginResponse>(`${environment.apiBaseUrl}/api/v1/auth/potato/callback`, {
        code,
        codeVerifier,
      })
    );
  }

  refreshAccessToken(refreshToken: string): Promise<PotatoLoginResponse> {
    return firstValueFrom(
      this.http.post<PotatoLoginResponse>(`${environment.apiBaseUrl}/api/v1/auth/refresh`, {
        refreshToken,
      })
    );
  }

  logout(refreshToken: string): Promise<LogoutResponse> {
    return firstValueFrom(
      this.http.post<LogoutResponse>(`${environment.apiBaseUrl}/api/v1/auth/logout`, {
        refreshToken,
      })
    );
  }

  clearPkceSession(): void {
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.codeVerifier);
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.oauthState);
  }

  getStoredOAuthState(): string | null {
    return sessionStorage.getItem(AUTH_STORAGE_KEYS.oauthState);
  }

  getStoredCodeVerifier(): string | null {
    return sessionStorage.getItem(AUTH_STORAGE_KEYS.codeVerifier);
  }
}
